import React, { useState } from 'react';
import { Drawer, List, ListItemButton, ListItemIcon, ListItemText, Toolbar, Box, Tooltip, Divider } from '@mui/material';
import { 
  Schedule as ScheduleIcon, 
  WorkOutline as WorkOutlineIcon,
  CalendarMonth as CalendarIcon,
  Settings as SettingsIcon,
  ListAlt as ListAltIcon,
  Badge as BadgeIcon,
  Group as GroupIcon,
  HeadsetMic as HeadsetMicIcon,
  Spa as SpaIcon,
  LocalHospital as LocalHospitalIcon,
  Inventory2Outlined as PackageIcon,
  ReceiptLong as BillingIcon,
  FitnessCenter as FitnessCenterIcon,
  LocalOffer as AddonPackageIcon,
  Hotel as RetreatPackageIcon,
  ManageAccounts as UserManagementIcon,
  Assessment as ReportsIcon,
  Build as MaintenanceIcon,
  Logout as LogoutIcon,
  Tune as SystemConfigIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import AppVersionLabel from './AppVersionLabel';

const drawerWidthCollapsed = 72;
const drawerWidthExpanded = 240;

const inactiveIconColor = 'rgba(255,255,255,0.9)';
const activeIconColor = '#ffffff';

const Navigation = ({ activeTab, onTabChange }) => {
  const { hasPermission, logout, user } = useAuth();
  const [expanded, setExpanded] = useState(false);

  // Define all navigation items with their permissions
  const navItems = [
    { key: 'scheduler', Icon: ScheduleIcon, label: 'Scheduler', permission: 'scheduler:view' },
    { key: 'packages', Icon: PackageIcon, label: 'Packages', permission: 'packages:view' },
    { key: 'guests', Icon: GroupIcon, label: 'Guests', permission: 'guests:view' },
    { key: 'itinerary', Icon: ListAltIcon, label: 'Itinerary', permission: 'itinerary:view' },
    { key: 'duty', Icon: BadgeIcon, label: 'Duty', permission: 'duty:view' },
    { key: 'reception', Icon: HeadsetMicIcon, label: 'Reception', permission: 'reception:view' },
    { key: 'therapist', Icon: SpaIcon, label: 'Therapist', permission: 'therapist:view' },
    { key: 'doctor', Icon: LocalHospitalIcon, label: 'Doctor', permission: 'doctor:view' },
    { key: 'billing', Icon: BillingIcon, label: 'Billing', permission: 'billing:view' },
    { key: 'addonPackages', Icon: AddonPackageIcon, label: 'Addons', permission: 'addonPackages:view' },
    { key: 'retreatPackages', Icon: RetreatPackageIcon, label: 'Retreats', permission: 'retreatPackages:view' },
    { key: 'activities', Icon: FitnessCenterIcon, label: 'Activities', permission: 'activities:view' },
    { key: 'shifts', Icon: WorkOutlineIcon, label: 'Shifts', permission: 'shifts:view' },
    { key: 'roster', Icon: CalendarIcon, label: 'Roster', permission: 'roster:view' },
    { key: 'maintenance', Icon: MaintenanceIcon, label: 'Maintenance', permission: 'maintenance:view' },
    { key: 'reports', Icon: ReportsIcon, label: 'Reports', permission: 'reports:view' },
    { key: 'userManagement', Icon: UserManagementIcon, label: 'Users', permission: 'user_management:view' },
    { key: 'systemConfig', Icon: SystemConfigIcon, label: 'System Config', permission: 'admin:view' },
    { key: 'admin', Icon: SettingsIcon, label: 'Admin', permission: 'admin:view' },
  ];

  // Filter items based on permissions
  const allowedItems = navItems.filter(item => {
    // Special case: Roster can be accessed with either roster:view OR scheduler:view
    if (item.key === 'roster') {
      return hasPermission('roster', 'view') || hasPermission('scheduler', 'view');
    }
    const [screen, action] = item.permission.split(':');
    return hasPermission(screen, action);
  });

  const handleLogout = async () => {
    await logout();
  };

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: expanded ? drawerWidthExpanded : drawerWidthCollapsed,
        flexShrink: 0,
        transition: 'width 0.3s ease',
        '& .MuiDrawer-paper': {
          width: expanded ? drawerWidthExpanded : drawerWidthCollapsed,
          boxSizing: 'border-box',
          background: 'linear-gradient(180deg, #C7AE6E 0%, #9F8752 100%)',
          color: '#fff',
          borderRight: '1px solid rgba(0,0,0,0.12)',
          display: 'flex',
          flexDirection: 'column',
          transition: 'width 0.3s ease',
          overflowX: 'hidden'
        },
      }}
    >
      {/* Logo - clickable to expand/collapse */}
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: expanded ? 'flex-start' : 'center', 
          alignItems: 'center', 
          pt: '10px', 
          pb: 1,
          px: expanded ? 2 : 0,
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          '&:hover': {
            opacity: 0.8
          }
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Box
          component="img"
          src="/atmantan-logo-a.png"
          alt="Atmantan"
          sx={{ width: 44, height: 'auto', opacity: 0.95 }}
        />
        {expanded && (
          <Box sx={{ ml: 2, fontWeight: 600, fontSize: '1.1rem', whiteSpace: 'nowrap' }}>
            Atmantan
          </Box>
        )}
      </Box>
      <Toolbar />
      
      {/* Navigation Items */}
      <Box 
        sx={{ 
          flexGrow: 1, 
          overflow: 'auto',
          // Custom scrollbar styling
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'rgba(199, 174, 110, 0.3)', // Match sidebar color but lighter
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(199, 174, 110, 0.6)', // Match sidebar color
            borderRadius: '3px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: 'rgba(199, 174, 110, 0.8)', // Slightly darker on hover
          },
          // For Firefox
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(199, 174, 110, 0.6) rgba(199, 174, 110, 0.3)',
        }}
      >
        <List>
          {allowedItems.map(({ key, Icon, label }) => (
            <Tooltip key={key} title={expanded ? '' : label} placement="right" disableHoverListener={expanded}>
              <ListItemButton 
                selected={activeTab === key} 
                onClick={() => onTabChange(key)} 
                sx={{ 
                  py: 2, 
                  justifyContent: expanded ? 'flex-start' : 'center',
                  px: expanded ? 2 : 0,
                  borderRadius: 0, 
                  mx: 0, 
                  '&.Mui-selected': { backgroundColor: '#B49A5F' }, 
                  '&.Mui-selected:hover': { backgroundColor: '#A3894F' }, 
                  '&:hover': { background: 'rgba(255,255,255,0.06)' } 
                }}
              >
                <ListItemIcon sx={{ 
                  minWidth: expanded ? 40 : 0, 
                  color: activeTab === key ? activeIconColor : inactiveIconColor,
                  justifyContent: 'center'
                }}>
                  <Icon />
                </ListItemIcon>
                {expanded && (
                  <ListItemText 
                    primary={label} 
                    sx={{ 
                      color: activeTab === key ? activeIconColor : inactiveIconColor,
                      '& .MuiListItemText-primary': {
                        fontWeight: activeTab === key ? 600 : 400
                      }
                    }} 
                  />
                )}
              </ListItemButton>
            </Tooltip>
          ))}
        </List>
      </Box>

      {/* User Info and Logout */}
      <Box sx={{ pb: 2 }}>
        <AppVersionLabel expanded={expanded} />
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.2)', mb: 1 }} />
        <Tooltip title={expanded ? '' : `${user?.email || 'User'} - Click to logout`} placement="right" disableHoverListener={expanded}>
          <ListItemButton
            onClick={handleLogout}
            sx={{
              py: 2,
              px: expanded ? 2 : 0,
              justifyContent: expanded ? 'flex-start' : 'center',
              color: inactiveIconColor,
              borderRadius: 0,
              '&:hover': {
                background: 'rgba(255,255,255,0.08)',
                color: activeIconColor
              }
            }}
          >
            <ListItemIcon sx={{ 
              minWidth: expanded ? 40 : 0,
              color: inactiveIconColor,
              justifyContent: 'center'
            }}>
              <LogoutIcon />
            </ListItemIcon>
            {expanded && (
              <ListItemText 
                primary="Logout"
                secondary={user?.email || 'User'}
                sx={{
                  '& .MuiListItemText-primary': {
                    color: inactiveIconColor,
                    fontWeight: 500
                  },
                  '& .MuiListItemText-secondary': {
                    color: 'rgba(255,255,255,0.6)',
                    fontSize: '0.75rem'
                  }
                }}
              />
            )}
          </ListItemButton>
        </Tooltip>
      </Box>
    </Drawer>
  );
};

export default Navigation;
